import React, { createContext, useContext, useEffect, useState } from 'react';
import useOnboardingStepCounter from '../../../utils/onboardingStepCounter';
import { selectUser } from '../../../reducers/user.selectors';
import { useSelector } from 'react-redux';
import useIsNewSession from '../../../utils/useIsNewSession';
import useScreenSize from '../../../utils/useScreenSize';

type OnboardingContextType = Omit<ReturnType<typeof useOnboardingStepCounter>, 'dismiss'> & {
  showWalkthrough: boolean;
  showCourseHint: boolean;
  showSearchOnboarding: boolean;
  dismissWalkthrough: () => void;
  dismissCourseHint: () => void;
  dismissSearchOnboarding: () => void;
};

const NewNavigationOnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const NAVIGATION_WALKTHROUGH_SEEN_KEY = 'new-navigation-walkthrough-seen';
const NAVIGATION_COURSE_HINT_SEEN_KEY = 'new-navigation-course-hint-seen';
const NAVIGATION_SEARCH_ONBOARDING_SEEN_KEY = 'new-navigation-search-onboarding-seen';
const NAVIGATION_SESSION_KEY = 'new-navigation-session';

export const NewNavigationOnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { step, startOnboarding, incrementStep, decrementStep } = useOnboardingStepCounter();
  const user = useSelector(selectUser);
  const isNewSession = useIsNewSession(NAVIGATION_SESSION_KEY);
  const { isLargeScreen, isSmallScreen, isMediumScreen } = useScreenSize();

  const hasSeenWalkthrough = localStorage.getItem(NAVIGATION_WALKTHROUGH_SEEN_KEY) === 'true';
  const hasSeenCourseHint = localStorage.getItem(NAVIGATION_COURSE_HINT_SEEN_KEY) === 'true';
  const hasSeenSearchOnboarding = localStorage.getItem(NAVIGATION_SEARCH_ONBOARDING_SEEN_KEY) === 'true';

  const isUserTypeTwo = user?.type === 2;

  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [showCourseHint, setShowCourseHint] = useState(false);
  const [showSearchOnboarding, setShowSearchOnboarding] = useState(false);

  useEffect(() => {
    if (user) {
      const shouldShowWalkthrough = !hasSeenWalkthrough && isUserTypeTwo;
      setShowWalkthrough(shouldShowWalkthrough);

      setShowCourseHint(!hasSeenCourseHint && isUserTypeTwo && isNewSession && !shouldShowWalkthrough);
      setShowSearchOnboarding(!hasSeenSearchOnboarding && isUserTypeTwo);
    }
  }, [user, hasSeenWalkthrough, hasSeenCourseHint, hasSeenSearchOnboarding, isUserTypeTwo, isNewSession]);

  const dismissWalkthrough = () => {
    localStorage.setItem(NAVIGATION_WALKTHROUGH_SEEN_KEY, 'true');
    setShowWalkthrough(false);
  };

  const dismissCourseHint = () => {
    localStorage.setItem(NAVIGATION_COURSE_HINT_SEEN_KEY, 'true');
    setShowCourseHint(false);
  };

  const dismissSearchOnboarding = () => {
    localStorage.setItem(NAVIGATION_SEARCH_ONBOARDING_SEEN_KEY, 'true');
    setShowSearchOnboarding(false);
  };

  useEffect(() => {
    if (step && ((isLargeScreen && step > 2) || ((isSmallScreen || isMediumScreen) && step > 2))) {
      dismissWalkthrough();
    }
  }, [step]);

  return (
    <NewNavigationOnboardingContext.Provider
      value={{
        step,
        startOnboarding,
        incrementStep,
        decrementStep,
        showWalkthrough,
        showCourseHint,
        showSearchOnboarding,
        dismissWalkthrough,
        dismissCourseHint,
        dismissSearchOnboarding,
      }}
    >
      {children}
    </NewNavigationOnboardingContext.Provider>
  );
};

export const useNewNavigationOnboarding = () => {
  const context = useContext(NewNavigationOnboardingContext);
  if (context === undefined) {
    throw new Error('useNewNavigationOnboarding must be used within a NewNavigationOnboardingProvider');
  }
  return context;
};
